import React, { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'

import { PRODUCT_TOUR_VERSION } from '../LessonUI/ProductTour/data'

/**
 * Custom hook for tracking which tab we want to display: the user can freely select the different
 * tabs, but if the product tour wants a tab to be visible, that takes precedence.
 */
function useTabState(initialSelectedTabName) {
  const [userSelectedTabName, setUserSelectedTabName] = useState(initialSelectedTabName)
  const [tourSelectedTabName, setTourSelectedTabName] = useState(null)

  function onActivate(source, tabName) {
    if (source === 'user') {
      setUserSelectedTabName(tabName)
    } else if (source === 'tour') {
      setTourSelectedTabName(tabName)
    }
  }

  return [tourSelectedTabName || userSelectedTabName, onActivate]
}

/**
 * Custom hook that resolves a client timestamp indicating the last time we saw any activity from
 * the assigned sandbox, or null if no sandbox is assigned and ready. If the sandbox has a proxy,
 * this is typically the most recent response timestamp; otherwise we fall back on the timestamp
 * when we were assigned a ready sandbox.
 */
function useSandboxLastActivityTimestamp() {
  // Sending a request through the proxy doesn't result in touchSandbox() being called, so we'll
  // ignore last proxy response time for now. TODO: When requests refresh the sandbox, use:
  // Math.max(sandboxReadyTimestamp, sbproxy.lastRecvTimestamp)
  return useSelector((state) => {
    const { sandbox } = state
    return sandbox.url && !sandbox.requestError ? sandbox.requestFinishedTimestamp : null
  })
}

/**
 * Custom hook that returns a boolean indicating whether the sandbox has been inactive for longer
 * than the specified interval (in milliseconds).
 */
function useSandboxInactivityTimeout(interval) {
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const lastActivityTimestamp = useSandboxLastActivityTimestamp()

  const timerHandle = useRef(null)
  const checkTimeout = () => {
    const elapsedSinceLastActivity = Date.now() - lastActivityTimestamp
    setHasTimedOut(elapsedSinceLastActivity >= interval)
  }

  useEffect(() => {
    if (timerHandle.current) {
      clearInterval(timerHandle.current)
      timerHandle.current = null
    }
    setHasTimedOut(false)
    if (typeof lastActivityTimestamp === 'number') {
      timerHandle.current = setInterval(checkTimeout, 5000)
    }
  }, [lastActivityTimestamp])

  return hasTimedOut
}

export { useTabState, useSandboxLastActivityTimestamp, useSandboxInactivityTimeout }
